/**
 * What counts as an acceptable image, checked in the browser.
 *
 * These rules mirror the backend's, which checks the bytes themselves. Nothing
 * here is a guard - a renamed file passes an extension check, and the declared
 * type comes from the browser - it is here so somebody who picks a PDF is told
 * so at once rather than after a slow upload that ends in a 415.
 */

/** PNG, JPEG and WebP. SVG is refused deliberately: it can carry scripts. */
export const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
export const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.webp'];
export const TYPES_IN_WORDS = 'PNG, JPEG or WebP';

export const LOGO_MAX_BYTES = 2 * 1024 * 1024;
export const COVER_MAX_BYTES = 5 * 1024 * 1024;

export function inMegabytes(bytes: number): string {
  return `${Math.round((bytes / (1024 * 1024)) * 10) / 10} MB`;
}

/**
 * Why a file was refused, or null when it is fine.
 *
 * Both the declared type and the file name are checked, because either alone is
 * weak: the type can be missing for an unusual file, and the extension is
 * trivially renamed.
 */
export function refuseFile(file: File, maxBytes: number): string | null {
  const name = file.name.toLowerCase();
  const extensionAllowed = IMAGE_EXTENSIONS.some((ext) => name.endsWith(ext));

  if (!IMAGE_TYPES.includes(file.type) || !extensionAllowed) {
    return `Choose a ${TYPES_IN_WORDS} image.`;
  }
  if (file.size > maxBytes) {
    return `That image is ${inMegabytes(file.size)}. Use one of ${inMegabytes(maxBytes)} or less.`;
  }
  return null;
}
